//
/* GS://PROTOCOLO
 * Arquivo: src/services/lotsService.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 1.7.0 – FIX: Precificação Manual e Custo Real
 * Impacto da Versão: [src/services/lotsService.js]
 * Data: 26/12/2025
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FIX]: Alterada a base de cálculo do Produto Integrado (Pronta Entrega).
 * - CUSTO: Passa a usar `costBruto` (Valor NF + Rateio de Despesas) em vez de `unitPriceNF`.
 * - PREÇO: Define `price: 0` para forçar o status "Aguardando Precificação".
 * - SUGESTÃO: Mantém `suggestedPrice` calculado (Custo * 2) apenas como referência.
 */
//

import { db } from '../firebase/config';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import productService from './productService'; // [GS-NOVO]: Import para lógica de SKU

/**
 * Referência base da coleção de lotes.
 */
const lotsCollectionRef = collection(db, 'lots');

/**
 * Converte um valor qualquer de data em string 'yyyy-MM-dd'.
 */
const toIsoDateString = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    try {
      return value.toDate().toISOString().slice(0, 10);
    } catch (err) {
      console.warn('[lotsService/toIsoDateString] Falha ao converter Timestamp:', err);
      return '';
    }
  }
  return '';
};

/**
 * Converte 'yyyy-MM-dd' em 'dd/MM/yyyy'.
 */
const toBrDateString = (isoDate) => {
  if (!isoDate || typeof isoDate !== 'string') return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return '';
  const [yyyy, mm, dd] = parts;
  return `${dd}/${mm}/${yyyy}`;
};

const normalizeTotals = (rawTotals) => {
  const t = rawTotals || {};
  return {
    subtotalItens: Number(t.subtotalItens ?? 0),
    totalRateio: Number(t.totalRateio ?? 0),
    totalGeral: Number(t.totalGeral ?? 0),
  };
};

const normalizeCounts = (rawData) => {
  if (rawData && rawData.counts) {
    const c = rawData.counts || {};
    return {
      totalItensNF: Number(c.totalItensNF ?? 0),
      qtdBrutas: Number(c.qtdBrutas ?? 0),
      qtdProntas: Number(c.qtdProntas ?? 0),
      qtdServicos: Number(c.qtdServicos ?? 0),
    };
  }
  const raw = Number(rawData?.totalRawItems ?? 0);
  const finished = Number(rawData?.totalFinishedItems ?? 0);
  const service = Number(rawData?.totalServiceItems ?? 0);
  return {
    totalItensNF: raw + finished + service,
    qtdBrutas: raw,
    qtdProntas: finished,
    qtdServicos: service,
  };
};

const mapLotDoc = (snap) => {
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  const lotCode = data.lotCode || data.lotNumber || '';
  const nfNumber = data.nfNumber || data.nfId || '';

  let nfDateISO = '';
  if (data.nfDate) {
    nfDateISO = toIsoDateString(data.nfDate);
  } else if (data.createdAt) {
    nfDateISO = toIsoDateString(data.createdAt);
  }
  const nfDateDisplay = nfDateISO ? toBrDateString(nfDateISO) : '';

  const supplierId = data.supplierId || null;
  const supplierName = data.supplierName || (supplierId ? '' : 'N/A');
  const counts = normalizeCounts(data);
  const totals = normalizeTotals(data.totals);
  const statusLot = data.statusLot || data.status || 'ABERTO';

  return {
    id: snap.id,
    lotCode,
    nfId: data.nfId || null,
    nfNumber,
    nfDate: nfDateDisplay,
    nfDateISO,
    nfDateDisplay,
    supplierId,
    supplierName,
    counts,
    totals,
    statusLot,
    createdAt: data.createdAt || null,
    createdBy: data.createdBy || data.createdByUserId || null,
  };
};

const mapLotItemDoc = (snap) => {
  if (!snap.exists()) return null;
  const data = snap.data() || {};

  const costTotalInicial = Number(data.costTotalInicial ?? 0);
  const costBrutoRaw = Number(data.costBruto ?? 0);
  const statusInicial = data.statusInicial || null;
  const statusPiece = data.statusPiece || statusInicial || null;

  return {
    id: snap.id,
    nfItemIndex: Number(data.nfItemIndex ?? 0),
    itemType: data.itemType || 'BRUTA',
    descriptionNF: data.descriptionNF || '',
    supplierProductCode: data.supplierProductCode || '',
    categoryId: data.categoryId || null,
    quantity: Number(data.quantity ?? 0),
    weightGrams: Number(data.weightGrams ?? 0),
    unitPriceNF: Number(data.unitPriceNF ?? 0),
    totalItemNF: Number(data.totalItemNF ?? 0),
    rateioPerItem: Number(data.rateioPerItem ?? 0),
    costTotalInicial,
    costBruto: costBrutoRaw || costTotalInicial,
    statusInicial,
    statusPiece,
    stockRef: data.stockRef || null,
    createdAt: data.createdAt || null,
  };
};

export const getLots = async () => {
  let qy;
  try {
    qy = query(lotsCollectionRef, orderBy('createdAt', 'desc'));
  } catch (err) {
    console.warn('[lotsService/getLots] Falha ao ordenar:', err);
    qy = lotsCollectionRef;
  }
  const snap = await getDocs(qy);
  return snap.docs.map((d) => mapLotDoc(d)).filter((lot) => lot !== null);
};

export const getLotById = async (lotId) => {
  if (!lotId) return null;
  const ref = doc(db, 'lots', lotId);
  const snap = await getDoc(ref);
  return mapLotDoc(snap);
};

export const getLotItems = async (lotId) => {
  if (!lotId) return [];
  const itemsCol = collection(db, 'lots', lotId, 'items');
  let qy;
  try {
    qy = query(itemsCol, orderBy('nfItemIndex', 'asc'));
  } catch (err) {
    console.warn('[lotsService/getLotItems] Falha ao ordenar:', err);
    qy = itemsCol;
  }
  const snap = await getDocs(qy);
  return snap.docs.map((d) => mapLotItemDoc(d)).filter((item) => item !== null);
};

// [GS-NOVO]: Integração de Itens Pronta Entrega ao Estoque
export const integrateReadyItemsToStock = async (lotId) => {
  if (!lotId) throw new Error("ID do lote obrigatório.");

  try {
    const lotItems = await getLotItems(lotId);
    const readyItems = lotItems.filter(item => item.itemType === 'PRONTA');

    if (readyItems.length === 0) {
      throw new Error("Nenhum item do tipo PRONTA encontrado neste lote.");
    }

    const batch = writeBatch(db);
    const productsRef = collection(db, 'products');
    const movementsRef = collection(db, 'inventory_movements');
    const lotRef = doc(db, 'lots', lotId);

    // Recupera dados do Lote para enriquecer histórico
    const lotDoc = await getDoc(lotRef);
    const lotData = lotDoc.data();
    const lotNumber = lotData.lotCode || lotData.lotNumber || 'S/N';

    for (const item of readyItems) {
      // 1. Gerar SKU Oficial
      // [GS-FIX]: Chamada ao productService para garantir SKU GS (Prefixo + Fornecedor + Sequencial)
      const officialSku = await productService.generateSmartSku(item.categoryId, lotData.supplierId);

      // 2. Criar Referência de Produto
      const newProductRef = doc(productsRef);
      const productId = newProductRef.id;

      // [GS-FIX]: Definição do Custo Real (Baseado em NF + Rateio)
      // O campo costBruto nos itens já contém (Unitário NF + Rateio). 
      // Caso costBruto seja zero ou nulo (legado), usa unitPriceNF como fallback.
      const realCost = Number(item.costBruto) > 0 ? Number(item.costBruto) : Number(item.unitPriceNF);

      // 3. Montar Objeto Produto (Estoque de Venda)
      const productData = {
        name: item.descriptionNF,
        supplierProductName: item.descriptionNF, // Nome técnico igual
        sku: officialSku, // [GS-FIX]: SKU gerado corretamente
        description: `Produto integrado do Lote ${lotNumber} - NF ${lotData.nfNumber}`,
        
        // [GS-FIX]: Preço Zero para forçar status "Aguardando Precificação"
        price: 0, 
        cost: realCost, // [GS-FIX]: Campo 'cost' agora reflete o valor contábil correto (NF + Rateio)
        suggestedPrice: realCost * 2, // Mantém a sugestão apenas para referência
        
        stock: item.quantity,
        category: 'Geral', // Será ajustado se categoryId for válido e populado no front
        categoryId: item.categoryId || null,
        
        isActive: true,
        pdvEnabled: false, // Entra desativado
        
        lotId: lotId,
        lotItemId: item.id,
        nfOriginId: lotData.nfId,
        nfNumber: lotData.nfNumber,
        
        supplierId: lotData.supplierId,
        
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        type: 'PRODUTO',
        
        // [GS-FIX]: Status explícito para o fluxo manual
        status: 'Aguardando Precificação',
        location: 'Em Loja (Aguardando)' 
      };

      batch.set(newProductRef, productData);

      // 4. Criar Movimentação de Estoque (Entrada)
      const moveRef = doc(movementsRef);
      batch.set(moveRef, {
        productId: productId,
        sku: officialSku,
        type: 'ENTRADA_NF', // ou 'NF_ENTRADA' conforme enum
        movementType: 'NF_ENTRADA',
        quantity: item.quantity,
        quantityDelta: item.quantity,
        previousStock: 0,
        newStock: item.quantity,
        reason: `Entrada via Lote ${lotNumber}`,
        lotId: lotId,
        nfId: lotData.nfId,
        createdAt: serverTimestamp(),
        createdBy: 'Sistema (Integração Lote)'
      });
    }

    // 5. Atualizar Status do Lote
    batch.update(lotRef, {
      statusLot: 'FINALIZADO',
      status: 'FINALIZADO',
      updatedAt: serverTimestamp()
    });

    await batch.commit();
    return { success: true, count: readyItems.length };

  } catch (error) {
    console.error('[integrateReadyItemsToStock] Erro:', error);
    throw error;
  }
};

const lotsService = {
  getLots,
  getLotById,
  getLotItems,
  integrateReadyItemsToStock, // Exportado
};

export default lotsService;